(() => {
  const DEFAULT_MANUAL_RULES = [
    {
      name: 'single_80g_flavour',
      max_unique_cart_items: 1,
      contains_any: ['80g', 'gymrat-jerky', 'smoky', 'bbq', 'miami', 'hot chilli', 'hot-chilli'],
      recommend: [
        'gymrat-jerky-hot-chilli-80g',
        'gymrat-jerky-miami-spice',
        'gymrat-jerky-variety-pack',
      ],
    },
    {
      name: 'five_pack',
      contains_any: ['5-pack', '5 pack', 'five-pack', 'twinpack'],
      recommend: [
        'gymrat-jerky-variety-pack',
        'gymrat-jerky-hot-chilli-80g',
        'gymrat-jerky-miami-spice',
        'gymrat-jerky',
      ],
    },
    {
      name: 'variety_pack',
      contains_any: ['variety'],
      recommend: [
        'gymrat-jerky',
        'gymrat-jerky-hot-chilli-80g',
        'gymrat-jerky-miami-spice',
      ],
    },
  ];

  const DEFAULT_FALLBACK_HANDLES = [
    'gymrat-jerky',
    'gymrat-jerky-hot-chilli-80g',
    'gymrat-jerky-miami-spice',
    'gymrat-jerky-variety-pack',
  ];

  const getConfig = () => {
    const config = window.GymratCartDrawerConfig || {};
    return {
      ...config,
      freeShippingThresholdCents: Number(config.freeShippingThresholdCents || 11000),
      freeGiftThresholdCents: Number(config.freeGiftThresholdCents || 16000),
      nzFreeGiftThresholdCents: Number(config.nzFreeGiftThresholdCents || 20000),
      countryCode: config.countryCode ? String(config.countryCode).toUpperCase() : '',
      freeGiftVariantId: config.freeGiftVariantId ? String(config.freeGiftVariantId) : '',
      freeGiftProductHandle: config.freeGiftProductHandle ? String(config.freeGiftProductHandle) : '',
      freeGiftProductId: config.freeGiftProductId ? String(config.freeGiftProductId) : '',
      freeGiftProductTitle: config.freeGiftProductTitle ? String(config.freeGiftProductTitle).toLowerCase() : '',
      shippingProtectionProductHandle: config.shippingProtectionProductHandle
        ? String(config.shippingProtectionProductHandle)
        : 'shipping-protection',
      shippingProtectionProductId: config.shippingProtectionProductId ? String(config.shippingProtectionProductId) : '7876653973642',
      shippingProtectionProductTitle: config.shippingProtectionProductTitle
        ? String(config.shippingProtectionProductTitle).toLowerCase()
        : 'shipping protection',
      shippingProtectionPriceCents: Number(config.shippingProtectionPriceCents || 249),
      hotChilliProductId: config.hotChilliProductId ? String(config.hotChilliProductId) : '8637911203978',
      preloadedUpsellProducts: Array.isArray(config.preloadedUpsellProducts) ? config.preloadedUpsellProducts : [],
      manualRules: parseManualRules(config.manualUpsellRules),
      fallbackHandles: parseHandleList(config.fallbackUpsellHandles, DEFAULT_FALLBACK_HANDLES),
    };
  };

  const parseManualRules = (rawRules) => {
    if (!rawRules || typeof rawRules !== 'string') return DEFAULT_MANUAL_RULES;

    try {
      const rules = JSON.parse(rawRules);
      return Array.isArray(rules) && rules.length ? rules : DEFAULT_MANUAL_RULES;
    } catch (error) {
      warn('Manual upsell rules are not valid JSON. Falling back to defaults.', error);
      return DEFAULT_MANUAL_RULES;
    }
  };

  const parseHandleList = (rawHandles, fallback = []) => {
    if (!rawHandles || typeof rawHandles !== 'string') return fallback;

    const handles = rawHandles
      .split(/[\n,]/)
      .map((handle) => handle.trim())
      .filter(Boolean);

    return handles.length ? handles : fallback;
  };

  const warn = (...messages) => {
    if (window.Shopify && window.Shopify.designMode) {
      console.warn('[Cart drawer]', ...messages);
    }
  };

  const escapeHTML = (value) => {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  };

  class CartDrawer extends HTMLElement {
    constructor() {
      super();

      this.config = getConfig();
      this.productCache = new Map();
      this.productIdCache = new Map();
      this.preloadedProducts = new Map();
      this.recommendationCache = new Map();
      this.requestCounter = 0;
      this.recommendationCounter = 0;
      this.noteTimer = null;
      this.isMutating = false;
      this.unlockState = null;
      this.freeGiftOptOutKey = 'gymratCartFreeGiftOptOut';
      this.suppressedExternalWidgets = new Map();
      this.popupSuppressionObserver = null;
      this.widgetSuppressionTimers = [];

      this.onBodyClick = this.onBodyClick.bind(this);
      this.onBodyChange = this.onBodyChange.bind(this);
      this.onBodyInput = this.onBodyInput.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentSubmit = this.onDocumentSubmit.bind(this);
      this.onKeyUp = this.onKeyUp.bind(this);
      this.seedPreloadedProducts();
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.panel = this.querySelector('.gym-cart__panel');
      this.body = this.querySelector('[data-cart-body]');
      this.footer = this.querySelector('[data-cart-footer]');
      this.error = this.querySelector('[data-cart-error]');
      this.busyIndicator = this.querySelector('[data-cart-busy]');
      this.busyText = this.querySelector('[data-cart-busy-text]');
      this.liveRegion = this.querySelector('[data-cart-live-region]');
      this.checkoutButton = this.querySelector('[data-cart-checkout]');

      this.addEventListener('click', this.onBodyClick);
      this.addEventListener('change', this.onBodyChange);
      this.addEventListener('input', this.onBodyInput);
      this.addEventListener('keyup', this.onKeyUp);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('submit', this.onDocumentSubmit, true);

      this.setHeaderCartIconAccessibility();
      this.refresh();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onBodyClick);
      this.removeEventListener('change', this.onBodyChange);
      this.removeEventListener('input', this.onBodyInput);
      this.removeEventListener('keyup', this.onKeyUp);
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('submit', this.onDocumentSubmit, true);
    }

    seedPreloadedProducts() {
      this.config.preloadedUpsellProducts.forEach((product) => {
        if (!product || !product.handle) return;
        this.preloadedProducts.set(product.handle, product);
        this.productCache.set(product.handle, product);
        if (product.id) this.productIdCache.set(String(product.id), product);
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
        this.refresh();
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() !== 'SPACE') return;
        event.preventDefault();
        this.open(cartLink);
        this.refresh();
      });
    }

    getSectionsToRender() {
      return [{ id: 'cart-icon-bubble' }];
    }

    renderContents(parsedState) {
      if (parsedState && parsedState.sections) {
        this.renderSectionCartIcon(parsedState.sections);
      }

      this.setLoading(true);
      this.open();
      this.refresh();
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);

      this.removeAttribute('aria-hidden');
      document.body.classList.add('overflow-hidden');
      document.body.classList.add('gym-cart-open');
      this.suppressExternalWidgets();
      this.observeExternalWidgets();
      this.queueExternalWidgetSuppression();

      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
        const focusElement = this.querySelector('[data-cart-close]') || this.panel;

        if (typeof trapFocus === 'function' && this.panel) {
          trapFocus(this.panel, focusElement);
        } else if (focusElement) {
          focusElement.focus();
        }
      });
    }

    close() {
      this.classList.remove('active');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('gym-cart-open');
      this.restoreExternalWidgets();
      this.stopObservingExternalWidgets();

      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.activeElement);
      } else if (this.activeElement) {
        this.activeElement.focus();
      }
    }

    onKeyUp(event) {
      if (event.code === 'Escape') this.close();
    }

    onDocumentClick(event) {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = event.target.closest?.('a[href]');
      if (!link || this.contains(link) || link.target === '_blank' || link.hasAttribute('download')) return;
      if (!this.isCartLink(link.getAttribute('href'))) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(link);
      this.refresh();
    }

    onDocumentSubmit(event) {
      const form = event.target;
      if (!form || String(form.tagName).toLowerCase() !== 'form' || this.contains(form)) return;
      if (!this.isCartAddForm(form)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (typeof form.checkValidity === 'function' && !form.checkValidity()) {
        if (typeof form.reportValidity === 'function') form.reportValidity();
        return;
      }

      this.addCartForm(form, event.submitter || form.querySelector('[type="submit"]'));
    }

    onBodyClick(event) {
      const closeButton = event.target.closest('[data-cart-close]');
      if (closeButton) {
        event.preventDefault();
        this.close();
        return;
      }

      const removeButton = event.target.closest('[data-cart-remove]');
      if (removeButton) {
        event.preventDefault();
        const line = removeButton.closest('[data-cart-line]');
        if (line?.dataset.freeGiftLine === 'true') this.setFreeGiftOptOut(true);
        this.changeLine(removeButton.dataset.key, 0, removeButton);
        return;
      }

      const quantityButton = event.target.closest('[data-cart-quantity-button]');
      if (quantityButton) {
        event.preventDefault();
        this.handleQuantityButton(quantityButton);
        return;
      }

      const upsellButton = event.target.closest('[data-upsell-add]');
      if (upsellButton) {
        event.preventDefault();
        const upsellCard = upsellButton.closest('[data-upsell-card]');
        const selectedVariant = upsellCard ? upsellCard.querySelector('[data-upsell-variant]') : null;
        this.addUpsell(selectedVariant?.value || upsellButton.dataset.variantId, upsellButton);
        return;
      }

      const tenPackSwitch = event.target.closest('[data-ten-pack-switch]');
      if (tenPackSwitch) {
        event.preventDefault();
        this.switchToTenPack(tenPackSwitch.dataset.key, tenPackSwitch.dataset.handle, tenPackSwitch);
        return;
      }

    }

    onBodyChange(event) {
      const shippingProtectionToggle = event.target.closest('[data-shipping-protection-toggle]');
      if (shippingProtectionToggle) {
        this.toggleShippingProtection(shippingProtectionToggle);
        return;
      }

      const upsellVariantSelect = event.target.closest('[data-upsell-variant]');
      if (upsellVariantSelect) {
        this.updateUpsellVariant(upsellVariantSelect);
        return;
      }

      const quantityInput = event.target.closest('[data-cart-quantity]');
      if (!quantityInput) return;

      const quantity = Math.max(0, Number.parseInt(quantityInput.value, 10) || 0);
      this.changeLine(quantityInput.dataset.key, quantity, quantityInput);
    }

    onBodyInput(event) {
      const noteInput = event.target.closest('[data-cart-note]');
      if (!noteInput) return;

      window.clearTimeout(this.noteTimer);
      this.noteTimer = window.setTimeout(() => {
        this.updateNote(noteInput.value);
      }, 450);
    }

    handleQuantityButton(button) {
      const line = button.closest('[data-cart-line]');
      const input = line ? line.querySelector('[data-cart-quantity]') : null;
      if (!input) return;

      const currentQuantity = Number.parseInt(input.value, 10) || 0;
      const direction = button.dataset.cartQuantityButton;
      const nextQuantity = direction === 'increase' ? currentQuantity + 1 : Math.max(0, currentQuantity - 1);

      input.value = nextQuantity;
      this.changeLine(input.dataset.key, nextQuantity, button);
    }

    async refresh(seedCart = null) {
      const requestId = ++this.requestCounter;

      try {
        let cart = seedCart || (await this.fetchCart());
        cart = await this.enforceFreeGiftRules(cart);
        cart = await this.enforceShippingProtectionRules(cart);
        if (requestId !== this.requestCounter) return;

        this.cart = cart;
        this.render(cart);
        this.publishCartUpdate(cart);
        this.loadEnhancements(cart);
        this.handleUnlockAnimations(cart);
        if (document.body.classList.contains('gym-cart-open')) this.queueExternalWidgetSuppression();
      } catch (error) {
        this.showError(error.message || window.cartStrings?.error || 'Cart could not be updated.');
      } finally {
        if (requestId === this.requestCounter) this.setLoading(false);
      }
    }

    async fetchCart() {
      return this.fetchJson(this.localizedPath('cart.js'));
    }

    async addUpsell(variantId, button) {
      if (!variantId || this.isMutating) return;

      await this.addVariant(variantId, 1, button);
    }

    async resolveFreeGiftVariantId() {
      if (this.config.freeGiftVariantId) return this.config.freeGiftVariantId;

      const preloadedGift = this.config.freeGiftProductId ? this.productIdCache.get(this.config.freeGiftProductId) : null;
      const preloadedProducts = [preloadedGift, ...this.preloadedProducts.values()].filter(Boolean);
      for (const product of preloadedProducts) {
        if (!this.isFreeGiftProduct(product)) continue;

        const variant = this.getFreeGiftVariant(product);
        if (variant) return String(variant.id);
      }

      const handles = [
        this.config.freeGiftProductHandle,
        'gymrat-jerky-hot-chilli-sample',
        'hot-chilli-sample',
        'gymrat-jerky-sample-hot-chilli',
      ].filter(Boolean);

      for (const handle of [...new Set(handles)]) {
        const product = await this.fetchProduct(handle);
        if (!product || !this.isFreeGiftProduct(product)) continue;

        const variant = this.getFreeGiftVariant(product);
        if (variant) return String(variant.id);
      }

      return '';
    }

    async resolveShippingProtectionVariantId() {
      const preloadedProtection = this.config.shippingProtectionProductId
        ? this.productIdCache.get(this.config.shippingProtectionProductId)
        : null;
      const preloadedProducts = [preloadedProtection, ...this.preloadedProducts.values()].filter(Boolean);
      for (const product of preloadedProducts) {
        if (!this.isShippingProtectionProduct(product)) continue;

        const variant = this.getShippingProtectionVariant(product);
        if (variant) return String(variant.id);
      }

      const handles = [
        this.config.shippingProtectionProductHandle,
        'shipping-protection',
        'ship-protect',
        'shipping-insurance',
      ].filter(Boolean);

      for (const handle of [...new Set(handles)]) {
        const product = await this.fetchProduct(handle);
        if (!product || !this.isShippingProtectionProduct(product)) continue;

        const variant = this.getShippingProtectionVariant(product);
        if (variant) return String(variant.id);
      }

      return '';
    }

    async addVariant(variantId, quantity, button) {
      this.clearError();
      this.setMutationLoading(true, 'Adding item...');
      this.setButtonLoading(button, true);
      this.isMutating = true;

      try {
        await this.fetchJson(this.localizedPath('cart/add.js'), {
          method: 'POST',
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity,
              },
            ],
          }),
        });
        this.announce('Added to cart.');
        await this.refresh();
      } catch (error) {
        this.showError(error.message || 'This item could not be added.');
      } finally {
        this.isMutating = false;
        this.setButtonLoading(button, false);
        this.setMutationLoading(false);
      }
    }

    async addCartForm(form, submitButton) {
      if (this.isMutating) return;

      this.clearError();
      this.setMutationLoading(true, 'Adding item...');
      this.setButtonLoading(submitButton, true);
      this.isMutating = true;

      const disabledIdInputs = [...form.querySelectorAll('[name="id"]:disabled')];
      disabledIdInputs.forEach((input) => {
        input.disabled = false;
      });

      try {
        const formData = new FormData(form);
        if (!formData.has('sections')) {
          formData.append(
            'sections',
            this.getSectionsToRender()
              .map((section) => section.id)
              .join(',')
          );
        }
        if (!formData.has('sections_url')) formData.append('sections_url', window.location.pathname);

        const response = await fetch(this.config.cartAddUrl || this.localizedPath('cart/add'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.status) {
          const errorMessage =
            data.description ||
            data.message ||
            data.errors ||
            window.cartStrings?.error ||
            'This item could not be added.';
          throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
        }

        if (data.sections) this.renderSectionCartIcon(data.sections);
        this.announce('Added to cart.');
        this.open(submitButton);
        await this.refresh();
      } catch (error) {
        this.open(submitButton);
        this.showError(error.message || 'This item could not be added.');
        await this.refresh();
      } finally {
        disabledIdInputs.forEach((input) => {
          input.disabled = true;
        });
        this.isMutating = false;
        this.setButtonLoading(submitButton, false);
        this.setMutationLoading(false);
      }
    }

    async changeLine(key, quantity, control) {
      if (!key || this.isMutating) return;

      this.clearError();
      this.setMutationLoading(true, quantity === 0 ? 'Removing item...' : 'Updating quantity...');
      this.setButtonLoading(control, true);
      this.isMutating = true;

      try {
        const updatedCart = await this.fetchJson(this.localizedPath('cart/change.js'), {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity }),
        });
        await this.refresh(updatedCart);
      } catch (error) {
        this.showError(error.message || window.cartStrings?.quantityError || 'Quantity could not be updated.');
        await this.refresh();
      } finally {
        this.isMutating = false;
        this.setButtonLoading(control, false);
        this.setMutationLoading(false);
      }
    }

    async switchToTenPack(key, handle, button) {
      if (!key || this.isMutating) return;

      this.clearError();
      this.setMutationLoading(true, 'Switching to 10-Pack...');
      this.setButtonLoading(button, true);
      this.isMutating = true;

      try {
        const cart = this.cart || (await this.fetchCart());
        const line = (cart.items || []).find((item) => item.key === key);
        if (!line) throw new Error('This cart item could not be found. Please refresh and try again.');

        const product = await this.fetchProduct(handle || line.handle);
        const tenPackVariant = this.getTenPackVariant(product, line.variant_id);
        if (!tenPackVariant) throw new Error('10-Pack variant could not be found for this product.');

        const quantity = Math.max(1, Number(line.quantity || 1));
        await this.fetchJson(this.localizedPath('cart/add.js'), {
          method: 'POST',
          body: JSON.stringify({
            items: [
              {
                id: Number(tenPackVariant.id),
                quantity,
              },
            ],
          }),
        });
        await this.fetchJson(this.localizedPath('cart/change.js'), {
          method: 'POST',
          body: JSON.stringify({ id: key, quantity: 0 }),
        });
        this.announce('Switched to 10-Pack.');
        await this.refresh();
      } catch (error) {
        this.showError(error.message || 'Could not switch this item to a 10-Pack.');
        await this.refresh();
      } finally {
        this.isMutating = false;
        this.setButtonLoading(button, false);
        this.setMutationLoading(false);
      }
    }

    async toggleShippingProtection(toggle) {
      if (this.isMutating) return;

      const shouldAdd = toggle.checked;
      const loadingIndicator = this.footer.querySelector('[data-shipping-protection-loading]');
      this.clearError();
      this.setMutationLoading(true, shouldAdd ? 'Adding shipping protection...' : 'Removing shipping protection...');
      toggle.disabled = true;
      if (loadingIndicator) loadingIndicator.hidden = false;
      this.isMutating = true;

      try {
        const cart = this.cart || (await this.fetchCart());
        const protectionLines = this.getShippingProtectionLines(cart);

        if (shouldAdd) {
          if (!protectionLines.length) {
            const variantId = await this.resolveShippingProtectionVariantId();
            if (!variantId) {
              toggle.checked = false;
              this.showError('Shipping Protection could not be loaded. Check the product handle.');
              return;
            }

            await this.fetchJson(this.localizedPath('cart/add.js'), {
              method: 'POST',
              body: JSON.stringify({
                items: [
                  {
                    id: Number(variantId),
                    quantity: 1,
                  },
                ],
              }),
            });
          }
        } else if (protectionLines.length) {
          await Promise.all(
            protectionLines.map((item) =>
              this.fetchJson(this.localizedPath('cart/change.js'), {
                method: 'POST',
                body: JSON.stringify({ id: item.key, quantity: 0 }),
              })
            )
          );
        }

        await this.refresh();
      } catch (error) {
        toggle.checked = !shouldAdd;
        this.showError(error.message || 'Shipping Protection could not be updated.');
        await this.refresh();
      } finally {
        this.isMutating = false;
        toggle.disabled = false;
        if (loadingIndicator) loadingIndicator.hidden = true;
        this.setMutationLoading(false);
      }
    }

    async updateNote(note) {
      try {
        await this.fetchJson(this.localizedPath('cart/update.js'), {
          method: 'POST',
          body: JSON.stringify({ note }),
        });
        if (this.cart) this.cart.note = note;
        this.announce('Order note saved.');
      } catch (error) {
        this.showError(error.message || 'Order note could not be saved.');
      }
    }

    async enforceFreeGiftRules(cart) {
      const gifts = this.getFreeGiftLines(cart);
      const qualifies = this.getSubtotal(cart) >= this.getFreeGiftThresholdCents();
      const hasGiftConfig =
        this.config.freeGiftVariantId ||
        this.config.freeGiftProductHandle ||
        this.config.freeGiftProductId ||
        this.config.freeGiftProductTitle;

      if (!hasGiftConfig) return cart;

      if (!qualifies) {
        this.setFreeGiftOptOut(false);
        if (!gifts.length) return cart;

        return this.runFreeGiftMutation('Removing free gift...', async () => {
          await Promise.all(
            gifts.map((item) =>
              this.fetchJson(this.localizedPath('cart/change.js'), {
                method: 'POST',
                body: JSON.stringify({ id: item.key, quantity: 0 }),
              })
            )
          );
          return this.fetchCart();
        });
      }

      if (!gifts.length) {
        if (this.hasFreeGiftOptOut()) return cart;

        const variantId = await this.resolveFreeGiftVariantId();
        if (!variantId) {
          warn('Free gift product could not be loaded. Check the free gift product handle.');
          return cart;
        }

        return this.runFreeGiftMutation('Adding free gift...', async () => {
          await this.fetchJson(this.localizedPath('cart/add.js'), {
            method: 'POST',
            body: JSON.stringify({
              items: [
                {
                  id: Number(variantId),
                  quantity: 1,
                },
              ],
            }),
          });
          return this.fetchCart();
        });
      }

      const extraGifts = gifts.slice(1);
      const firstGiftNeedsQuantityReset = Number(gifts[0].quantity || 0) !== 1;
      if (!extraGifts.length && !firstGiftNeedsQuantityReset) return cart;

      return this.runFreeGiftMutation('Updating free gift...', async () => {
        const updates = extraGifts.map((item) =>
          this.fetchJson(this.localizedPath('cart/change.js'), {
            method: 'POST',
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          })
        );

        if (firstGiftNeedsQuantityReset) {
          updates.push(
            this.fetchJson(this.localizedPath('cart/change.js'), {
              method: 'POST',
              body: JSON.stringify({ id: gifts[0].key, quantity: 1 }),
            })
          );
        }

        await Promise.all(updates);
        return this.fetchCart();
      });
    }

    async enforceShippingProtectionRules(cart) {
      const protectionLines = this.getShippingProtectionLines(cart);
      if (!protectionLines.length) return cart;

      const hasMerchandise = this.getMerchandiseItems(cart).length > 0;
      if (!hasMerchandise) {
        return this.runFreeGiftMutation('Removing shipping protection...', async () => {
          await Promise.all(
            protectionLines.map((item) =>
              this.fetchJson(this.localizedPath('cart/change.js'), {
                method: 'POST',
                body: JSON.stringify({ id: item.key, quantity: 0 }),
              })
            )
          );
          return this.fetchCart();
        });
      }

      const extraProtectionLines = protectionLines.slice(1);
      const firstLineNeedsQuantityReset = Number(protectionLines[0].quantity || 0) !== 1;
      if (!extraProtectionLines.length && !firstLineNeedsQuantityReset) return cart;

      return this.runFreeGiftMutation('Updating shipping protection...', async () => {
        const updates = extraProtectionLines.map((item) =>
          this.fetchJson(this.localizedPath('cart/change.js'), {
            method: 'POST',
            body: JSON.stringify({ id: item.key, quantity: 0 }),
          })
        );

        if (firstLineNeedsQuantityReset) {
          updates.push(
            this.fetchJson(this.localizedPath('cart/change.js'), {
              method: 'POST',
              body: JSON.stringify({ id: protectionLines[0].key, quantity: 1 }),
            })
          );
        }

        await Promise.all(updates);
        return this.fetchCart();
      });
    }

    async runFreeGiftMutation(message, callback) {
      const wasMutating = this.isMutating;
      this.isMutating = true;
      this.setMutationLoading(true, message);

      try {
        return await callback();
      } finally {
        this.isMutating = wasMutating;
        this.setMutationLoading(false);
      }
    }

    render(cart) {
      const displayItemCount = this.getDisplayCartItems(cart).length;
      this.classList.toggle('is-empty', displayItemCount === 0);
      this.clearError();

      this.body.innerHTML = [
        this.renderProgressArea(cart),
        displayItemCount ? this.renderItems(cart) : this.renderEmptyState(),
        displayItemCount ? this.renderUpsellLoadingState() : '',
        this.renderOrderNote(cart),
      ].join('');

      this.renderFooter(cart);
      this.renderCartIcon(cart);
    }

    renderProgressArea(cart) {
      if (this.isNewZealandRewardsMarket()) return this.renderNewZealandProgressArea(cart);

      const subtotal = this.getSubtotal(cart);
      const freeShippingThreshold = Number(this.config.freeShippingThresholdCents || 0);
      const freeGiftThreshold = Math.max(freeShippingThreshold, Number(this.config.freeGiftThresholdCents || 0));
      const shippingUnlocked = freeShippingThreshold <= 0 || subtotal >= freeShippingThreshold;
      const giftUnlocked = freeGiftThreshold <= 0 || subtotal >= freeGiftThreshold;
      const progress = this.getUnifiedRewardProgress(subtotal, freeShippingThreshold, freeGiftThreshold);
      const shippingRemaining = Math.max(0, freeShippingThreshold - subtotal);
      const giftRemaining = Math.max(0, freeGiftThreshold - subtotal);
      let message = 'Bang! You got it!';

      if (!shippingUnlocked) {
        message = `Only ${this.formatMoney(shippingRemaining)} more for free shipping.`;
      } else if (!giftUnlocked) {
        message = `Free shipping unlocked! Now, you're ${this.formatMoney(
          giftRemaining
        )} away from scoring a FREE bag of Gymrat Jerky: Hot Chilli (valued at $14.49).`;
      }

      return `
        <div class="gym-cart-progress-group">
          <div class="gym-cart-progress gym-cart-progress--unified${shippingUnlocked ? ' is-shipping-unlocked' : ''}${
        giftUnlocked ? ' is-unlocked' : ''
      }">
            <div class="gym-cart-progress__label gym-cart-progress__label--unified">
              <span>Rewards</span>
            </div>
            <div class="gym-cart-progress__track-wrap">
              <div class="gym-cart-progress__track" aria-hidden="true">
                <span class="gym-cart-progress__fill" style="width: ${progress}%"></span>
              </div>
              <span class="gym-cart-progress__marker gym-cart-progress__marker--shipping${
                shippingUnlocked ? ' is-active' : ''
              }" aria-hidden="true"></span>
              <span class="gym-cart-progress__marker gym-cart-progress__marker--gift${
                giftUnlocked ? ' is-active' : ''
              }" aria-hidden="true"></span>
            </div>
            <div class="gym-cart-progress__milestones" aria-hidden="true">
              <span class="gym-cart-progress__milestone gym-cart-progress__milestone--shipping">Free shipping</span>
              <span class="gym-cart-progress__milestone gym-cart-progress__milestone--gift">Free gift</span>
            </div>
            <p class="gym-cart-progress__message">${escapeHTML(message)}</p>
          </div>
        </div>
      `;
    }

    renderNewZealandProgressArea(cart) {
      const subtotal = this.getSubtotal(cart);
      const freeGiftThreshold = this.getFreeGiftThresholdCents();
      const giftUnlocked = freeGiftThreshold <= 0 || subtotal >= freeGiftThreshold;
      const progress = freeGiftThreshold > 0 ? Math.min(100, Math.round((subtotal / freeGiftThreshold) * 100)) : 100;
      const giftRemaining = Math.max(0, freeGiftThreshold - subtotal);
      const message = giftUnlocked
        ? 'Bang! You got it!'
        : `You're ${this.formatMoney(giftRemaining)} away from scoring a free bag of Gymrat Jerky: Hot Chilli.`;

      return `
        <div class="gym-cart-progress-group">
          <div class="gym-cart-progress gym-cart-progress--unified gym-cart-progress--gift-only${
            giftUnlocked ? ' is-unlocked' : ''
          }">
            <div class="gym-cart-progress__label gym-cart-progress__label--unified">
              <span>Free gift</span>
            </div>
            <div class="gym-cart-progress__track-wrap">
              <div class="gym-cart-progress__track" aria-hidden="true">
                <span class="gym-cart-progress__fill" style="width: ${progress}%"></span>
              </div>
              <span class="gym-cart-progress__marker gym-cart-progress__marker--gift${
                giftUnlocked ? ' is-active' : ''
              }" aria-hidden="true"></span>
            </div>
            <div class="gym-cart-progress__milestones" aria-hidden="true">
              <span class="gym-cart-progress__milestone gym-cart-progress__milestone--gift">Free gift</span>
            </div>
            <p class="gym-cart-progress__message">${escapeHTML(message)}</p>
          </div>
        </div>
      `;
    }

    getUnifiedRewardProgress(subtotal, freeShippingThreshold, freeGiftThreshold) {
      const currentSubtotal = Math.max(0, Number(subtotal || 0));
      const shippingThreshold = Math.max(0, Number(freeShippingThreshold || 0));
      const giftThreshold = Math.max(shippingThreshold, Number(freeGiftThreshold || 0));

      if (!giftThreshold) return 100;
      if (!shippingThreshold || giftThreshold === shippingThreshold) {
        return Math.min(100, Math.round((currentSubtotal / giftThreshold) * 100));
      }
      if (currentSubtotal < shippingThreshold) {
        return Math.min(50, Math.round((currentSubtotal / shippingThreshold) * 50));
      }
      if (currentSubtotal < giftThreshold) {
        const progressToGift = (currentSubtotal - shippingThreshold) / (giftThreshold - shippingThreshold);
        return Math.min(100, Math.round(50 + progressToGift * 50));
      }

      return 100;
    }

    renderProgress(label, subtotal, threshold, belowText, unlockedText) {
      const remaining = Math.max(0, threshold - subtotal);
      const progress = threshold > 0 ? Math.min(100, Math.round((subtotal / threshold) * 100)) : 100;
      const isUnlocked = remaining === 0;
      const message = isUnlocked ? unlockedText : belowText.replace('{{amount}}', this.formatMoney(remaining));

      return `
        <div class="gym-cart-progress${isUnlocked ? ' is-unlocked' : ''}" data-progress="${escapeHTML(label)}">
          <div class="gym-cart-progress__label">
            <span>${escapeHTML(label)}</span>
          </div>
          <div class="gym-cart-progress__track" aria-hidden="true">
            <span class="gym-cart-progress__fill" style="width: ${progress}%"></span>
          </div>
          <p class="gym-cart-progress__message">${escapeHTML(message)}</p>
        </div>
      `;
    }

    renderItems(cart) {
      const items = this.getDisplayCartItems(cart).reverse();
      const subtotal = this.getSubtotal(cart);

      return `
        <div class="gym-cart-items" data-cart-items>
          ${items.map((item) => this.renderItem(item, subtotal)).join('')}
        </div>
      `;
    }

    renderItem(item, subtotal = 0) {
      const isGift = this.isFreeGiftItem(item);
      const image = this.getItemImage(item);
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const hasDiscount = Number(item.original_line_price) > Number(item.final_line_price);
      const originalLinePrice = this.formatMoney(item.original_line_price);
      const finalLinePrice = this.formatMoney(item.final_line_price);

      return `
        <article class="gym-cart-item${isGift ? ' gym-cart-item--gift' : ''}" data-cart-line data-key="${escapeHTML(
          item.key
        )}" data-variant-id="${escapeHTML(item.variant_id)}" data-handle="${escapeHTML(
          item.handle || ''
        )}" data-free-gift-line="${isGift ? 'true' : 'false'}">
          <a class="gym-cart-item__image-wrap" href="${escapeHTML(item.url || '#')}" tabindex="-1" aria-hidden="true">
            ${
              image
                ? `<img class="gym-cart-item__image" src="${escapeHTML(image)}" alt="${escapeHTML(
                    item.featured_image?.alt || item.product_title || item.title
                  )}" loading="lazy">`
                : '<span class="gym-cart-item__image-placeholder"></span>'
            }
          </a>

          <div class="gym-cart-item__details">
            <div class="gym-cart-item__topline">
              <a class="gym-cart-item__title" href="${escapeHTML(item.url || '#')}">${escapeHTML(
                item.product_title || item.title
              )}</a>
              <div class="gym-cart-item__prices">
                <s data-line-compare ${hasDiscount ? '' : 'hidden'}>${hasDiscount ? originalLinePrice : ''}</s>
                <strong data-line-price>${finalLinePrice}</strong>
              </div>
            </div>

            ${variantTitle ? `<p class="gym-cart-item__variant">${escapeHTML(variantTitle)}</p>` : ''}
            ${this.renderProperties(item)}
            <small class="gym-cart-item__saving" data-line-saving hidden></small>

            <div class="gym-cart-item__actions">
              ${
                isGift
                  ? '<span class="gym-cart-item__gift-badge">Free gift</span>'
                  : this.renderQuantityControls(item)
              }
              <button class="gym-cart-item__remove" type="button" data-cart-remove data-key="${escapeHTML(item.key)}">
                Remove
              </button>
            </div>
          </div>
          ${!isGift ? this.renderTenPackSwitch(item, subtotal) : ''}
        </article>
      `;
    }

    renderTenPackSwitch(item, subtotal = 0) {
      if (this.isNewZealandRewardsMarket()) return '';

      const freeShippingThreshold = Number(this.config.freeShippingThresholdCents || 0);
      if (freeShippingThreshold && Number(subtotal || 0) >= freeShippingThreshold) return '';
      if (this.isSpartanSoapItem(item)) return '';
      if (!this.isFivePackItem(item)) return '';

      return `
        <div class="gym-cart-ten-pack" data-ten-pack-switch-widget>
          <span class="gym-cart-ten-pack__text">Switch to a 10-Pack and get free shipping?</span>
          <button
            class="gym-cart-ten-pack__button"
            type="button"
            data-ten-pack-switch
            data-key="${escapeHTML(item.key)}"
            data-handle="${escapeHTML(item.handle || '')}"
          >
            SWITCH
          </button>
        </div>
      `;
    }

    renderQuantityControls(item) {
      return `
        <div class="gym-cart-qty" aria-label="Quantity for ${escapeHTML(item.product_title || item.title)}">
          <button class="gym-cart-qty__button" type="button" data-cart-quantity-button="decrease" aria-label="Decrease quantity">-</button>
          <input
            class="gym-cart-qty__input"
            type="number"
            min="0"
            inputmode="numeric"
            value="${escapeHTML(item.quantity)}"
            data-cart-quantity
            data-key="${escapeHTML(item.key)}"
            aria-label="Quantity"
          >
          <button class="gym-cart-qty__button" type="button" data-cart-quantity-button="increase" aria-label="Increase quantity">+</button>
        </div>
      `;
    }

    renderProperties(item) {
      const propertyRows = [];

      if (item.properties && typeof item.properties === 'object') {
        Object.entries(item.properties).forEach(([name, value]) => {
          if (!value || String(name).startsWith('_')) return;
          propertyRows.push(`${escapeHTML(name)}: ${escapeHTML(value)}`);
        });
      }

      if (!propertyRows.length) return '';

      return `<p class="gym-cart-item__properties">${propertyRows.join('<br>')}</p>`;
    }

    renderOrderNote(cart) {
      if (!this.config.showOrderNote) return '';

      return `
        <div class="gym-cart-note">
          <label for="GymCartNote">Order note</label>
          <textarea id="GymCartNote" name="note" data-cart-note rows="3" placeholder="Add me a delivery note or flavour change request.">${escapeHTML(
            cart.note || ''
          )}</textarea>
        </div>
      `;
    }

    renderUpsellLoadingState() {
      return `
        <section class="gym-cart-upsells gym-cart-upsells--loading" data-upsells>
          <div class="gym-cart-upsells__header">
            <h3>What Other Lifters Are Getting</h3>
          </div>
          <p class="gym-cart-upsells__status">Loading recommendations...</p>
        </section>
      `;
    }

    renderEmptyState() {
      return `
        <div class="gym-cart-empty">
          <h3>No gains yet :(</h3>
          <p>Add some items and your cart will update here instantly</p>
          <a class="button" href="https://mithrasnutrition.com/collections/products">Shop products</a>
        </div>
      `;
    }

    renderFooter(cart, savings = null) {
      const subtotal = Number(cart.items_subtotal_price ?? cart.total_price ?? 0);
      const total = Number(cart.total_price || subtotal);
      const isEmpty = this.getDisplayCartItems(cart).length === 0;
      const hasShippingProtection = this.getShippingProtectionLines(cart).length > 0;

      this.footer.classList.toggle('is-empty', isEmpty);
      this.footer.querySelector('[data-cart-subtotal]').textContent = this.formatMoney(subtotal);

      const savingsRow = this.footer.querySelector('[data-cart-savings-row]');
      const savingsAmount = this.footer.querySelector('[data-cart-savings]');
      if (savings && savings > 0) {
        savingsRow.hidden = false;
        savingsAmount.textContent = this.formatMoney(savings);
      } else {
        savingsRow.hidden = true;
        savingsAmount.textContent = '';
      }

      if (this.checkoutButton) {
        this.checkoutButton.textContent = isEmpty ? 'Checkout' : `Checkout \u2022 ${this.formatMoney(total)}`;
        this.checkoutButton.href = this.config.checkoutUrl || this.localizedPath('checkout');
        this.checkoutButton.classList.toggle('is-disabled', isEmpty);
        this.checkoutButton.toggleAttribute('aria-disabled', isEmpty);
        this.checkoutButton.tabIndex = isEmpty ? -1 : 0;
      }

      const protectionWidget = this.footer.querySelector('[data-shipping-protection-widget]');
      const protectionToggle = this.footer.querySelector('[data-shipping-protection-toggle]');
      if (protectionWidget) protectionWidget.hidden = isEmpty;
      if (protectionToggle) protectionToggle.checked = hasShippingProtection;
    }

    async loadEnhancements(cart) {
      const recommendationId = ++this.recommendationCounter;

      try {
        await Promise.all([this.applyCompareAtSavings(cart, recommendationId), this.applyRecommendations(cart, recommendationId)]);
      } catch (error) {
        warn('Enhancement loading failed.', error);
      }
    }

    async applyCompareAtSavings(cart, recommendationId) {
      const productsByHandle = await this.fetchCartProducts(cart);
      if (recommendationId !== this.recommendationCounter) return;

      let totalSavings = 0;

      this.getDisplayCartItems(cart).forEach((item) => {
        const product = productsByHandle.get(item.handle);
        const variant = product?.variants?.find((candidate) => String(candidate.id) === String(item.variant_id));
        const compareAt = Number(variant?.compare_at_price || 0);
        const originalUnitPrice = Number(item.original_price || item.price || 0);
        const discountSavings = Math.max(0, Number(item.original_line_price || 0) - Number(item.final_line_price || 0));
        const compareSavings = compareAt > originalUnitPrice ? (compareAt - originalUnitPrice) * Number(item.quantity || 1) : 0;
        const lineSavings = compareSavings + discountSavings;
        const lineCompareAt = compareAt > originalUnitPrice ? compareAt * Number(item.quantity || 1) : Number(item.original_line_price || 0);

        totalSavings += lineSavings;
        this.updateLineSavings(item.key, lineCompareAt, Number(item.final_line_price || 0), lineSavings);
      });

      this.renderFooter(cart, totalSavings);
    }

    async fetchCartProducts(cart) {
      const productsByHandle = new Map();
      const handles = [...new Set(this.getDisplayCartItems(cart).map((item) => item.handle).filter(Boolean))];

      await Promise.all(
        handles.map(async (handle) => {
          const product = await this.fetchProduct(handle);
          if (product) productsByHandle.set(handle, product);
        })
      );

      return productsByHandle;
    }

    updateLineSavings(key, lineCompareAt, finalLinePrice, lineSavings) {
      const line = [...this.querySelectorAll('[data-cart-line]')].find((element) => element.dataset.key === key);
      if (!line) return;

      const compareElement = line.querySelector('[data-line-compare]');
      const savingElement = line.querySelector('[data-line-saving]');

      if (lineCompareAt > finalLinePrice) {
        compareElement.hidden = false;
        compareElement.textContent = this.formatMoney(lineCompareAt);
      } else {
        compareElement.hidden = true;
        compareElement.textContent = '';
      }

      if (lineSavings > 0) {
        savingElement.hidden = false;
        savingElement.textContent = `Save ${this.formatMoney(lineSavings)}`;
      } else {
        savingElement.hidden = true;
        savingElement.textContent = '';
      }
    }

    async applyRecommendations(cart, recommendationId) {
      const cacheKey = this.getDisplayCartItems(cart)
        .map((item) => item.handle)
        .filter(Boolean)
        .sort()
        .join('|');

      if (this.recommendationCache.has(cacheKey)) {
        this.renderRecommendations(this.recommendationCache.get(cacheKey));
        return;
      }

      const manualHandles = this.resolveManualRecommendationHandles(cart);
      let products = this.filterRecommendationProducts(await this.fetchProductsByHandle(manualHandles), cart);

      if (!products.length) {
        products = this.filterRecommendationProducts(await this.fetchShopifyRecommendationProducts(cart), cart);
      }

      if (recommendationId !== this.recommendationCounter) return;

      const filteredProducts = products.slice(0, 4);
      this.recommendationCache.set(cacheKey, filteredProducts);
      this.renderRecommendations(filteredProducts);
    }

    resolveManualRecommendationHandles(cart) {
      const displayItems = this.getDisplayCartItems(cart);
      const cartHandles = displayItems.map((item) => item.handle).filter(Boolean);
      const cartTitles = displayItems.map((item) => `${item.product_title || item.title || ''}`.toLowerCase());
      const uniqueItemCount = new Set(displayItems.map((item) => item.product_id)).size;

      const matchingRule = this.config.manualRules.find((rule) => {
        if (rule.max_unique_cart_items && uniqueItemCount > Number(rule.max_unique_cart_items)) return false;

        const tokens = rule.contains_any || rule.match_handles || rule.match_title_contains || [];
        if (!tokens.length) return true;

        return tokens.some((token) => {
          const normalizedToken = String(token).toLowerCase();
          return (
            cartHandles.some((handle) => handle === normalizedToken || handle.includes(normalizedToken)) ||
            cartTitles.some((title) => title.includes(normalizedToken))
          );
        });
      });

      return matchingRule?.recommend?.length ? matchingRule.recommend : this.config.fallbackHandles;
    }

    async fetchProductsByHandle(handles) {
      const products = [];

      for (const handle of [...new Set(handles.filter(Boolean))]) {
        const product = await this.fetchProduct(handle);
        if (product) products.push(product);
      }

      return products;
    }

    async fetchShopifyRecommendations(productId) {
      try {
        const url = `${this.localizedPath('recommendations/products.json')}?product_id=${encodeURIComponent(
          productId
        )}&limit=8&intent=related`;
        const data = await this.fetchJson(url);
        return data.products || [];
      } catch (error) {
        warn('Shopify recommendations failed.', error);
        return [];
      }
    }

    async fetchShopifyRecommendationProducts(cart) {
      const productIds = [
        ...this.getDisplayCartItems(cart).map((item) => item.product_id).filter(Boolean),
        this.config.hotChilliProductId,
      ].filter(Boolean);

      for (const productId of [...new Set(productIds)]) {
        const products = await this.fetchShopifyRecommendations(productId);
        if (products.length) return products;
      }

      return [];
    }

    filterRecommendationProducts(products, cart) {
      const displayItems = this.getDisplayCartItems(cart);
      const cartHandles = new Set(displayItems.map((item) => item.handle).filter(Boolean));
      const cartProductIds = new Set(displayItems.map((item) => String(item.product_id)));

      return products.filter((product, index, allProducts) => {
        const firstAvailableVariant = this.getFirstAvailableVariant(product);
        if (!product || !firstAvailableVariant) return false;
        if (cartHandles.has(product.handle) || cartProductIds.has(String(product.id))) return false;
        if (this.isFreeGiftProduct(product) || this.isShippingProtectionProduct(product)) return false;

        return allProducts.findIndex((candidate) => candidate.handle === product.handle) === index;
      });
    }

    renderRecommendations(products) {
      const section = this.querySelector('[data-upsells]');
      if (!section) return;

      if (!products.length) {
        section.hidden = false;
        section.classList.remove('gym-cart-upsells--loading');
        section.classList.add('gym-cart-upsells--empty');
        section.innerHTML = `
          <div class="gym-cart-upsells__header">
            <h3>What Other Lifters Are Getting</h3>
          </div>
          <p class="gym-cart-upsells__status">Recommendations are loading. Check the upsell product handles if this stays empty.</p>
        `;
        return;
      }

      section.hidden = false;
      section.classList.remove('gym-cart-upsells--loading', 'gym-cart-upsells--empty');
      section.innerHTML = `
        <div class="gym-cart-upsells__header">
          <h3>What Other Lifters Are Getting</h3>
        </div>
        <div class="gym-cart-upsells__list">
          ${products.map((product) => this.renderRecommendation(product)).join('')}
        </div>
      `;
    }

    renderRecommendation(product) {
      const variant = this.getFirstAvailableVariant(product);
      const image = this.getProductImage(product, variant);
      const price = Number(variant.price || product.price || 0);
      const compareAt = this.getVariantCompareAt(variant, price);
      const productUrl = this.localizedPath(`products/${product.handle}`);

      return `
        <article class="gym-cart-upsell" data-upsell-card>
          <a class="gym-cart-upsell__image-wrap" href="${escapeHTML(productUrl)}" tabindex="-1" aria-hidden="true">
            ${
              image
                ? `<img class="gym-cart-upsell__image" data-upsell-image src="${escapeHTML(image)}" alt="${escapeHTML(
                    product.featured_image?.alt || product.title
                  )}" loading="lazy">`
                : '<span class="gym-cart-item__image-placeholder"></span>'
            }
          </a>
          <div class="gym-cart-upsell__content">
            <a class="gym-cart-upsell__title" href="${escapeHTML(productUrl)}">${escapeHTML(product.title)}</a>
            <div class="gym-cart-upsell__prices">
              <s data-upsell-compare ${compareAt > price ? '' : 'hidden'}>${compareAt > price ? this.formatMoney(compareAt) : ''}</s>
              <strong data-upsell-price>${this.formatMoney(price)}</strong>
            </div>
            ${this.renderRecommendationVariantSelector(product, variant)}
            <a class="gym-cart-upsell__details" href="${escapeHTML(productUrl)}">View details</a>
          </div>
          <button class="gym-cart-upsell__add" type="button" data-upsell-add data-variant-id="${escapeHTML(variant.id)}">
            Add
          </button>
        </article>
      `;
    }

    renderRecommendationVariantSelector(product, selectedVariant) {
      const variants = product.variants || [];
      if (variants.length <= 1) return '';

      return `
        <label class="gym-cart-upsell__variant-label">
          <span class="visually-hidden">Choose option for ${escapeHTML(product.title)}</span>
          <select class="gym-cart-upsell__variant-select" data-upsell-variant>
            ${variants
              .map((variant) => {
                const image = this.getProductImage(product, variant);
                const isSelected = String(variant.id) === String(selectedVariant.id);
                return `
                  <option
                    value="${escapeHTML(variant.id)}"
                    data-price="${escapeHTML(variant.price || 0)}"
                    data-compare-at="${escapeHTML(this.getVariantCompareAt(variant, Number(variant.price || 0)))}"
                    data-image="${escapeHTML(image || '')}"
                    ${variant.available === false ? 'disabled' : ''}
                    ${isSelected ? 'selected' : ''}
                  >
                    ${escapeHTML(variant.title || 'Default')}
                  </option>
                `;
              })
              .join('')}
          </select>
        </label>
      `;
    }

    updateUpsellVariant(select) {
      const card = select.closest('[data-upsell-card]');
      const selectedOption = select.selectedOptions[0];
      if (!card || !selectedOption) return;

      const price = Number(selectedOption.dataset.price || 0);
      const compareAt = Number(selectedOption.dataset.compareAt || 0);
      const image = selectedOption.dataset.image || '';
      const addButton = card.querySelector('[data-upsell-add]');
      const priceElement = card.querySelector('[data-upsell-price]');
      const compareElement = card.querySelector('[data-upsell-compare]');
      const imageElement = card.querySelector('[data-upsell-image]');

      if (addButton) addButton.dataset.variantId = selectedOption.value;
      if (priceElement) priceElement.textContent = this.formatMoney(price);
      if (compareElement) {
        compareElement.hidden = compareAt <= price;
        compareElement.textContent = compareAt > price ? this.formatMoney(compareAt) : '';
      }
      if (imageElement && image) imageElement.src = image;
    }

    async fetchProduct(handle) {
      if (!handle) return null;
      if (this.productCache.has(handle)) return this.productCache.get(handle);
      if (this.preloadedProducts.has(handle)) {
        const product = this.preloadedProducts.get(handle);
        this.productCache.set(handle, product);
        return product;
      }

      try {
        const product = await this.fetchJson(this.localizedPath(`products/${handle}.js`));
        this.productCache.set(handle, product);
        return product;
      } catch (error) {
        warn(`Could not load product "${handle}".`, error);
        this.productCache.set(handle, null);
        return null;
      }
    }

    getFirstAvailableVariant(product) {
      if (!product?.variants?.length) return null;
      return product.variants.find((variant) => variant.available !== false) || null;
    }

    getTenPackVariant(product, currentVariantId) {
      if (!product?.variants?.length) return null;

      return (
        product.variants.find(
          (variant) =>
            String(variant.id) !== String(currentVariantId) &&
            variant.available !== false &&
            this.matchesPackSize(this.getVariantPackText(variant), 10)
        ) || null
      );
    }

    getVariantCompareAt(variant, price) {
      const compareAt = Number(variant?.compare_at_price || 0);
      const variantPrice = Number(price || variant?.price || 0);
      return compareAt > variantPrice ? compareAt : 0;
    }

    getFreeGiftVariant(product) {
      if (!product?.variants?.length) return null;
      return product.variants.find((variant) => variant.available !== false && Number(variant.price || 0) === 0) || null;
    }

    getShippingProtectionVariant(product) {
      if (!product?.variants?.length) return null;
      return (
        product.variants.find(
          (variant) =>
            variant.available !== false && Number(variant.price || 0) === this.config.shippingProtectionPriceCents
        ) ||
        product.variants.find((variant) => variant.available !== false) ||
        null
      );
    }

    handleUnlockAnimations(cart) {
      const subtotal = this.getSubtotal(cart);
      const hasFreeShippingReward = !this.isNewZealandRewardsMarket();
      const nextUnlockState = {
        freeShipping: hasFreeShippingReward && subtotal >= this.config.freeShippingThresholdCents,
        freeGift: subtotal >= this.getFreeGiftThresholdCents(),
      };

      if (this.unlockState) {
        const unlockedShipping = !this.unlockState.freeShipping && nextUnlockState.freeShipping;
        const unlockedGift = !this.unlockState.freeGift && nextUnlockState.freeGift;
        if (unlockedShipping || unlockedGift) this.launchGreenConfetti();
      }

      this.unlockState = nextUnlockState;
    }

    isNewZealandRewardsMarket() {
      const currency = String(this.config.currency || window.Shopify?.currency?.active || '').toUpperCase();
      return this.config.countryCode === 'NZ' || currency === 'NZD';
    }

    getFreeGiftThresholdCents() {
      return this.isNewZealandRewardsMarket()
        ? this.config.nzFreeGiftThresholdCents
        : this.config.freeGiftThresholdCents;
    }

    launchGreenConfetti() {
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

      const existingConfetti = document.querySelector('[data-gym-cart-confetti]');
      if (existingConfetti) existingConfetti.remove();

      const confetti = document.createElement('div');
      confetti.className = 'gym-cart-confetti';
      confetti.setAttribute('data-gym-cart-confetti', '');
      confetti.setAttribute('aria-hidden', 'true');

      const colors = ['#0f6b2f', '#18a64a', '#29b45a', '#74d680', '#d8f7d1'];
      const pieces = 34;

      for (let index = 0; index < pieces; index += 1) {
        const piece = document.createElement('span');
        piece.className = 'gym-cart-confetti__piece';
        piece.style.setProperty('--x', `${Math.random() * 100}%`);
        piece.style.setProperty('--drift', `${Math.random() * 140 - 70}px`);
        piece.style.setProperty('--delay', `${Math.random() * 0.22}s`);
        piece.style.setProperty('--duration', `${1.875 + Math.random() * 1.05}s`);
        piece.style.setProperty('--size', `${6 + Math.random() * 8}px`);
        piece.style.setProperty('--spin', `${360 + Math.random() * 720}deg`);
        piece.style.setProperty('--color', colors[index % colors.length]);
        confetti.appendChild(piece);
      }

      document.body.appendChild(confetti);
      window.setTimeout(() => confetti.remove(), 3600);
    }

    getItemImage(item) {
      return item.featured_image?.url || item.image || '';
    }

    getProductImage(product, variant) {
      if (variant?.featured_image?.src) return variant.featured_image.src;
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image?.src) return product.featured_image.src;
      return product.images?.[0] || '';
    }

    isFivePackItem(item) {
      if (this.isFreeGiftItem(item) || this.isShippingProtectionItem(item)) return false;
      return this.matchesPackSize(this.getCartItemPackText(item), 5);
    }

    isSpartanSoapItem(item) {
      const handle = String(item.handle || '').toLowerCase();
      const title = String(item.product_title || item.title || '').toLowerCase();
      const url = String(item.url || '').toLowerCase();

      return handle === 'spartan-soap' || title.includes('spartan soap') || url.includes('/products/spartan-soap');
    }

    getCartItemPackText(item) {
      const optionValues = Array.isArray(item.options_with_values)
        ? item.options_with_values.map((option) => option.value || option.name || '')
        : [];

      return [item.variant_title, item.title, ...optionValues].filter(Boolean).join(' ');
    }

    getVariantPackText(variant) {
      return [variant?.title, variant?.option1, variant?.option2, variant?.option3].filter(Boolean).join(' ');
    }

    matchesPackSize(value, size) {
      const text = String(value || '').toLowerCase();
      const pattern = new RegExp(`(^|[^a-z0-9])${size}\\s*[- ]?\\s*pack(s)?([^a-z0-9]|$)`, 'i');
      return pattern.test(text);
    }

    getSubtotal(cart) {
      return this.getMerchandiseItems(cart).reduce((subtotal, item) => {
        return subtotal + Number(item.final_line_price ?? item.line_price ?? 0);
      }, 0);
    }

    getDisplayCartItems(cart) {
      return (cart.items || []).filter((item) => !this.isShippingProtectionItem(item));
    }

    getMerchandiseItems(cart) {
      return (cart.items || []).filter((item) => !this.isFreeGiftItem(item) && !this.isShippingProtectionItem(item));
    }

    getFreeGiftLines(cart) {
      return cart.items.filter((item) => this.isFreeGiftItem(item));
    }

    getShippingProtectionLines(cart) {
      return (cart.items || []).filter((item) => this.isShippingProtectionItem(item));
    }

    isFreeGiftItem(item) {
      return (
        (this.config.freeGiftVariantId && String(item.variant_id) === this.config.freeGiftVariantId) ||
        (this.config.freeGiftProductId && String(item.product_id) === this.config.freeGiftProductId) ||
        (this.config.freeGiftProductTitle &&
          String(item.product_title || item.title || '').toLowerCase() === this.config.freeGiftProductTitle) ||
        String(item.product_type || '').toLowerCase() === 'free_gift'
      );
    }

    isFreeGiftProduct(product) {
      const freeGiftHandle = this.config.freeGiftProductHandle;
      const freeGiftTitle = this.config.freeGiftProductTitle;
      const tags = Array.isArray(product.tags) ? product.tags.map((tag) => String(tag).toLowerCase()) : [];
      const type = String(product.type || product.product_type || '').toLowerCase();

      return (
        (freeGiftHandle && product.handle === freeGiftHandle) ||
        (this.config.freeGiftProductId && String(product.id) === this.config.freeGiftProductId) ||
        (freeGiftTitle && String(product.title || '').toLowerCase() === freeGiftTitle) ||
        type === 'free_gift' ||
        tags.includes('free_gift')
      );
    }

    isShippingProtectionItem(item) {
      const title = String(item.product_title || item.title || '').toLowerCase();
      const type = String(item.product_type || '').toLowerCase();

      return (
        (this.config.shippingProtectionProductId && String(item.product_id) === this.config.shippingProtectionProductId) ||
        (this.config.shippingProtectionProductHandle && item.handle === this.config.shippingProtectionProductHandle) ||
        (this.config.shippingProtectionProductTitle && title === this.config.shippingProtectionProductTitle) ||
        type === 'shipping_protection'
      );
    }

    isShippingProtectionProduct(product) {
      const title = String(product.title || '').toLowerCase();
      const type = String(product.type || product.product_type || '').toLowerCase();

      return (
        (this.config.shippingProtectionProductId && String(product.id) === this.config.shippingProtectionProductId) ||
        (this.config.shippingProtectionProductHandle && product.handle === this.config.shippingProtectionProductHandle) ||
        (this.config.shippingProtectionProductTitle && title === this.config.shippingProtectionProductTitle) ||
        type === 'shipping_protection'
      );
    }

    hasFreeGiftOptOut() {
      try {
        return window.sessionStorage?.getItem(this.freeGiftOptOutKey) === 'true';
      } catch (error) {
        return false;
      }
    }

    setFreeGiftOptOut(isOptedOut) {
      try {
        if (isOptedOut) {
          window.sessionStorage?.setItem(this.freeGiftOptOutKey, 'true');
        } else {
          window.sessionStorage?.removeItem(this.freeGiftOptOutKey);
        }
      } catch (error) {
        warn('Could not update free gift opt-out state.', error);
      }
    }

    renderCartIcon(cart) {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      let bubble = cartLink.querySelector('.cart-count-bubble');
      const visibleItemCount = this.getDisplayCartItems(cart).reduce((count, item) => count + Number(item.quantity || 0), 0);
      if (!visibleItemCount) {
        if (bubble) bubble.remove();
        return;
      }

      if (!bubble) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        cartLink.appendChild(bubble);
      }

      bubble.innerHTML = `
        ${visibleItemCount < 100 ? `<span aria-hidden="true">${visibleItemCount}</span>` : ''}
        <span class="visually-hidden">${visibleItemCount} items</span>
      `;
    }

    renderSectionCartIcon(sections) {
      const html = sections['cart-icon-bubble'];
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!html || !cartLink) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const section = parsed.querySelector('.shopify-section');
      if (section) cartLink.innerHTML = section.innerHTML;
    }

    localizedPath(path) {
      if (/^https?:\/\//.test(path)) return path;

      const root = this.config.rootUrl || window.Shopify?.routes?.root || '/';
      const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
      const normalizedPath = path.startsWith('/') ? path.slice(1) : path;

      return `${normalizedRoot}${normalizedPath}`;
    }

    isCartLink(href) {
      if (!href) return false;

      try {
        const url = new URL(String(href).trim(), window.location.origin);
        if (url.origin !== window.location.origin) return false;
        return url.pathname.replace(/\/+$/, '').endsWith('/cart');
      } catch (error) {
        return false;
      }
    }

    isCartAddForm(form) {
      const action = form.getAttribute('action') || form.action || '';
      if (!action) return false;

      try {
        const url = new URL(String(action).trim(), window.location.origin);
        if (url.origin !== window.location.origin) return false;
        const path = url.pathname.replace(/\/+$/, '');
        return path.endsWith('/cart/add') || path.endsWith('/cart/add.js');
      } catch (error) {
        return false;
      }
    }

    async fetchJson(url, options = {}) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(options.headers || {}),
        },
        ...options,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status) {
        const errorMessage =
          data.description ||
          data.message ||
          data.errors ||
          window.cartStrings?.error ||
          'Cart request failed. Please try again.';
        throw new Error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }

      return data;
    }

    formatMoney(cents) {
      const currency = this.config.currency || window.Shopify?.currency?.active || 'AUD';
      const locale = this.config.locale || document.documentElement.lang || 'en-AU';
      const formatted = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
      }).format((Number(cents) || 0) / 100);

      return currency === 'AUD' ? formatted.replace(/^A\$/, '$') : formatted;
    }

    setLoading(isLoading) {
      this.classList.toggle('is-loading', isLoading);
      if (this.panel) this.panel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    setMutationLoading(isLoading, message = 'Updating cart...') {
      this.classList.toggle('is-mutating', isLoading);
      if (this.busyIndicator) this.busyIndicator.hidden = !isLoading;
      if (this.busyText) this.busyText.textContent = message;
      if (this.panel) this.panel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    setButtonLoading(button, isLoading) {
      if (!button) return;
      button.disabled = isLoading;
      button.classList.toggle('is-loading', isLoading);
    }

    clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      this.error.textContent = '';
    }

    showError(message) {
      if (!this.error) return;
      this.error.hidden = false;
      this.error.textContent = message;
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = message;
    }

    publishCartUpdate(cart) {
      if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: cart });
      }
    }

    suppressExternalWidgets() {
      const selectors = [
        '[id*="free-shipping" i]',
        '[class*="free-shipping" i]',
        '[id*="free_shipping" i]',
        '[class*="free_shipping" i]',
        '[id*="freeshipping" i]',
        '[class*="freeshipping" i]',
        '[id*="shipping-bear" i]',
        '[class*="shipping-bear" i]',
        '[id*="shipping_bar" i]',
        '[class*="shipping_bar" i]',
        '[id*="shipping-bar" i]',
        '[class*="shipping-bar" i]',
        '[id*="shippingbar" i]',
        '[class*="shippingbar" i]',
        '[id*="fsb" i]',
        '[class*="fsb" i]',
        '[id*="klaviyo" i]',
        '[class*="klaviyo" i]',
        '[class*="kl-private" i]',
        '.needsclick',
      ];

      const candidates = new Set([
        ...document.querySelectorAll(selectors.join(',')),
        ...document.body.children,
        ...document.querySelectorAll('body *'),
      ]);

      candidates.forEach((element) => {
        if (element.matches('[data-gym-cart-confetti]') || element.closest('[data-gym-cart-confetti]')) return;
        if (this.contains(element) || element.closest('cart-drawer')) return;
        if (!this.shouldSuppressExternalWidget(element, selectors)) return;

        if (!this.suppressedExternalWidgets.has(element)) {
          this.suppressedExternalWidgets.set(element, element.style.display || '');
        }

        element.style.display = 'none';
      });
    }

    shouldSuppressExternalWidget(element, selectors) {
      if (!(element instanceof HTMLElement)) return false;
      if (element.matches('[data-gym-cart-confetti]') || element.closest('[data-gym-cart-confetti]')) return false;

      const selectorMatch = selectors.some((selector) => {
        try {
          return element.matches(selector);
        } catch (error) {
          return false;
        }
      });

      const text = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      const styles = window.getComputedStyle(element);
      const zIndex = Number.parseInt(styles.zIndex, 10) || 0;
      const isOverlay = styles.position === 'fixed' || styles.position === 'sticky' || zIndex >= 900;
      if (selectorMatch && isOverlay) return true;
      if (!text && !isOverlay) return false;

      const looksLikeFreeShippingBear =
        text.includes('free shipping') ||
        text.includes('shipping unlocked') ||
        (text.includes('away') && text.includes('shipping'));
      const looksLikeKlaviyoDiscount = text.includes('unlock') && text.includes('mystery discount');
      if (isOverlay && zIndex >= 900 && !element.closest('cart-drawer')) return true;
      if (!looksLikeFreeShippingBear && !looksLikeKlaviyoDiscount) return false;

      return isOverlay || zIndex >= 50;
    }

    restoreExternalWidgets() {
      this.suppressedExternalWidgets.forEach((display, element) => {
        element.style.display = display;
      });
      this.suppressedExternalWidgets.clear();
    }

    queueExternalWidgetSuppression() {
      this.clearExternalWidgetSuppressionTimers();

      [0, 80, 240, 600, 1200, 2000].forEach((delay) => {
        const timer = window.setTimeout(() => this.suppressExternalWidgets(), delay);
        this.widgetSuppressionTimers.push(timer);
      });
    }

    clearExternalWidgetSuppressionTimers() {
      this.widgetSuppressionTimers.forEach((timer) => window.clearTimeout(timer));
      this.widgetSuppressionTimers = [];
    }

    observeExternalWidgets() {
      if (this.popupSuppressionObserver) return;

      this.popupSuppressionObserver = new MutationObserver(() => {
        if (document.body.classList.contains('gym-cart-open')) this.suppressExternalWidgets();
      });
      this.popupSuppressionObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class', 'style', 'id'],
        characterData: true,
        childList: true,
        subtree: true,
      });
    }

    stopObservingExternalWidgets() {
      this.clearExternalWidgetSuppressionTimers();
      if (!this.popupSuppressionObserver) return;

      this.popupSuppressionObserver.disconnect();
      this.popupSuppressionObserver = null;
    }
  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', CartDrawer);
  }
})();
